import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Switch,
} from '@mui/material';
import { Add, ChevronLeft, ChevronRight, Close, ExpandLess, FilterList, Search } from '@mui/icons-material';
import { AxiosError } from 'axios';
import apiClient from '../utils/apiClient';
import { useAuth } from '../hooks/useAuth';
import authService from '../services/authService';
import { DsBadge, DsButton, DsField } from '../design-system/components';
import './ManagementList.css';

type Role = 'administrator' | 'doctor' | 'operator';
type Account = {
  id: number;
  name: string;
  email: string;
  role: Role;
  doctor_id: number | null;
  doctor_name: string | null;
  is_active: boolean;
  admin_auth_method: 'google' | 'email_otp' | null;
  created_at: string;
  updated_at: string;
};
type Form = {
  name: string;
  email: string;
  password: string;
  role: Role;
  doctor_id: number | '';
  is_active: boolean;
};
type Mode = 'detail' | 'create' | 'edit';
const roleLabels: Record<Role, string> = {
  administrator: '管理者', doctor: '医師', operator: 'オペレーター',
};
const emptyForm: Form = { name: '', email: '', password: '', role: 'operator', doctor_id: '', is_active: true };
const authLabel = (account: Account, emailOtpEnabled: boolean | null) => account.role === 'doctor' ? '医師画面（対象外）' : emailOtpEnabled === null ? '確認中' : emailOtpEnabled ? '2FA（パスワード＋メールコード）' : 'パスワード';
const formatDate = (value: string | null) => value ? new Date(value).toLocaleString('ja-JP') : '—';
const errorMessage = (error: unknown): string => {
  const response = (error as AxiosError<{ message?: string; errors?: Record<string, string[]> }>).response;
  const first = response?.data?.errors && Object.values(response.data.errors)[0]?.[0];
  return first || response?.data?.message || '処理に失敗しました。時間をおいて再度お試しください。';
};

const UserManagementPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [draftRole, setDraftRole] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState<Mode | null>(null);
  const [selected, setSelected] = useState<Account | null>(null);
  const [form, setForm] = useState<Form>(emptyForm);
  const [doctorOptions, setDoctorOptions] = useState<{ id: number; label: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const [emailOtpEnabled, setEmailOtpEnabled] = useState<boolean | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.get<{ data: Account[]; meta: { last_page: number; total: number } }>('/api/admin/users', {
        params: { page, search: appliedSearch || undefined, role: roleFilter || undefined },
      });
      setAccounts(response.data.data);
      setPageCount(response.data.meta.last_page);
      setTotal(response.data.meta.total);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [page, appliedSearch, roleFilter]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { authService.getConfig().then(config => setEmailOtpEnabled(config.email_otp_enabled)).catch(() => setEmailOtpEnabled(null)); }, []);
  useEffect(() => {
    apiClient.get<{ data: { id: number; label: string }[] }>('/api/admin/users/doctor-options')
      .then(response => setDoctorOptions(response.data.data))
      .catch(() => setDoctorOptions([]));
  }, []);

  const openCreate = () => {
    setSelected(null); setForm(emptyForm); setDialogError(''); setMode('create');
  };
  const openAccount = async (account: Account, nextMode: Mode) => {
    setDialogError('');
    try {
      const response = await apiClient.get<{ data: Account }>(`/api/admin/users/${account.id}`);
      const item = response.data.data;
      setSelected(item);
      setForm({ name: item.name, email: item.email, password: '', role: item.role, doctor_id: item.doctor_id ?? '', is_active: item.is_active });
      setMode(nextMode);
    } catch (err) { setError(errorMessage(err)); }
  };
  const save = async () => {
    if (!form.name.trim() || !form.email.trim() || (mode === 'create' && form.password.length < 8)) {
      setDialogError('名前・メールアドレスと、必要な場合は8文字以上のパスワードを入力してください。'); return;
    }
    if (form.role === 'doctor' && !form.doctor_id) {
      setDialogError('医師アカウントには医師の紐づけが必要です。'); return;
    }
    setSaving(true); setDialogError('');
    const payload = {
      name: form.name.trim(), email: form.email.trim(), role: form.role,
      is_active: form.is_active, doctor_id: form.role === 'doctor' ? form.doctor_id : null,
      ...(form.password ? { password: form.password } : {}),
    };
    try {
      if (mode === 'create') await apiClient.post('/api/admin/users', payload);
      else if (selected) await apiClient.put(`/api/admin/users/${selected.id}`, payload);
      setMode(null);
      await load();
    } catch (err) { setDialogError(errorMessage(err)); }
    finally { setSaving(false); }
  };

  const applySearch = () => { setPage(1); setAppliedSearch(search.trim()); setRoleFilter(draftRole); };
  const clearFilters = () => { setSearch(''); setAppliedSearch(''); setDraftRole(''); setRoleFilter(''); setPage(1); };

  return <div className="management-page">
    <div className="management-heading"><div><h1>ユーザー管理</h1><p>ログイン用アカウントの確認と管理</p></div><div className="management-heading-actions"><DsButton tone="primary" icon={<Add />} onClick={openCreate}>ユーザーを追加</DsButton></div></div>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    <section className="management-panel" aria-label="ユーザーの検索と絞り込み">
      <form className="management-controls" role="search" onSubmit={event => { event.preventDefault(); applySearch(); }}>
        <label className="management-search"><Search fontSize="small" /><input aria-label="ユーザーを検索" placeholder="名前・メールアドレスで検索" value={search} onChange={event => setSearch(event.target.value)} /></label>
        <DsButton type="submit" tone="primary">検索</DsButton>
        <DsButton type="button" icon={filtersOpen ? <ExpandLess /> : <FilterList />} aria-expanded={filtersOpen} aria-controls="user-detail-filters" onClick={() => { if (filtersOpen) setDraftRole(roleFilter); setFiltersOpen(!filtersOpen); }}>{filtersOpen ? '詳細絞り込みを閉じる' : '詳細絞り込み'}{roleFilter && <span className="management-filter-count">1</span>}</DsButton>
      </form>
      {filtersOpen && <div id="user-detail-filters" className="management-filter-fields"><label>権限<select value={draftRole} onChange={event => setDraftRole(event.target.value)}><option value="">すべて</option>{Object.entries(roleLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label><p className="management-filter-help">条件を選び、上の「検索」で適用します</p></div>}
      {(appliedSearch || roleFilter) && <div className="management-applied"><span>適用中</span>{appliedSearch && <button type="button" onClick={() => { setSearch(''); setAppliedSearch(''); setPage(1); }}>検索: {appliedSearch} ×</button>}{roleFilter && <button type="button" onClick={() => { setDraftRole(''); setRoleFilter(''); setPage(1); }}>権限: {roleLabels[roleFilter as Role]} ×</button>}<button type="button" onClick={clearFilters}>すべてクリア</button></div>}
    </section>
    <section className="management-panel" aria-label="ユーザー一覧">
      <div className="management-results-head"><h2>検索結果</h2><span>{total} 件のユーザー</span></div>
      <div className="management-table-wrap"><table className="management-table"><thead><tr><th>名前</th><th>メールアドレス</th><th>権限</th><th>認証方式</th><th>状態</th><th>操作</th></tr></thead><tbody>{accounts.map(account => <tr key={account.id}><td><button className="management-link" onClick={() => void openAccount(account, 'detail')}>{account.name}</button></td><td>{account.email}</td><td>{roleLabels[account.role]}</td><td>{authLabel(account, emailOtpEnabled)}</td><td><DsBadge tone={account.is_active ? 'success' : 'neutral'}>{account.is_active ? '有効' : '無効'}</DsBadge></td><td><div className="management-row-actions"><button onClick={() => void openAccount(account, 'detail')}>詳細</button><button onClick={() => void openAccount(account, 'edit')}>編集</button></div></td></tr>)}</tbody></table></div>
      <div className="management-mobile-list">{accounts.map(account => <button key={account.id} className="management-mobile-item" onClick={() => void openAccount(account, 'detail')}><span className="management-mobile-top"><span className="management-link">{account.email}</span><DsBadge tone={account.is_active ? 'success' : 'neutral'}>{account.is_active ? '有効' : '無効'}</DsBadge></span><span className="management-mobile-name">{account.name}</span><span className="management-mobile-meta">{roleLabels[account.role]} · {authLabel(account, emailOtpEnabled)}</span></button>)}</div>
      {loading && <div className="management-empty"><CircularProgress size={22} /></div>}
      {!loading && accounts.length === 0 && <div className="management-empty">該当するユーザーはいません</div>}
      <div className="management-pagination"><span>全 {total} 件</span><div className="management-pagination-actions"><button type="button" aria-label="前のページ" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft fontSize="small" /></button><span>{page} / {Math.max(1, pageCount)}</span><button type="button" aria-label="次のページ" disabled={page >= pageCount} onClick={() => setPage(page + 1)}><ChevronRight fontSize="small" /></button></div></div>
    </section>
    <Dialog className="management-dialog" open={mode !== null} onClose={() => !saving && setMode(null)} fullWidth maxWidth="sm">
      <DialogTitle>{mode === 'create' ? 'ユーザーを追加' : mode === 'edit' ? 'ユーザーを編集' : 'ユーザー詳細'}</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {dialogError && <Alert severity="error" sx={{ m: 2 }}>{dialogError}</Alert>}
        {mode === 'detail' && selected ? <div className="management-detail-grid"><div><span>名前</span><strong>{selected.name}</strong></div><div><span>メールアドレス</span><strong>{selected.email}</strong></div><div><span>権限</span><strong>{roleLabels[selected.role]}</strong></div><div><span>認証方式</span><strong>{authLabel(selected, emailOtpEnabled)}</strong></div>{selected.role === 'doctor' && <div><span>紐づく医師</span><strong>{selected.doctor_name || selected.doctor_id || '—'}</strong></div>}<div><span>状態</span><strong>{selected.is_active ? '有効' : '無効'}</strong></div><div><span>登録日時</span><strong>{formatDate(selected.created_at)}</strong></div><div><span>更新日時</span><strong>{formatDate(selected.updated_at)}</strong></div></div> : mode !== null && <div className="management-form-grid">
          <DsField label="名前 *"><input className="ds-input" required value={form.name} onChange={event => setForm({ ...form, name: event.target.value })} /></DsField>
          <DsField label="メールアドレス *"><input className="ds-input" type="email" required value={form.email} onChange={event => setForm({ ...form, email: event.target.value })} /></DsField>
          <DsField label={mode === 'create' ? 'パスワード（8文字以上） *' : '新しいパスワード（変更する場合のみ）'}><input className="ds-input" type="password" autoComplete="new-password" value={form.password} onChange={event => setForm({ ...form, password: event.target.value })} /></DsField>
          <DsField label="権限"><select className="ds-select" value={form.role} onChange={event => setForm({ ...form, role: event.target.value as Role, doctor_id: event.target.value === 'doctor' ? form.doctor_id : '' })}>{Object.entries(roleLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></DsField>
          {form.role === 'doctor' && <DsField label="紐づく医師 *"><select className="ds-select" required value={form.doctor_id} onChange={event => setForm({ ...form, doctor_id: Number(event.target.value) })}><option value="">選択してください</option>{doctorOptions.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}</select></DsField>}
          <div className="management-wide"><FormControlLabel control={<Switch checked={form.is_active} disabled={selected?.id === currentUser?.id} onChange={event => setForm({ ...form, is_active: event.target.checked })} />} label="ログインを有効にする" /></div>
        </div>}
      </DialogContent>
      <DialogActions><DsButton type="button" onClick={() => setMode(null)} disabled={saving} icon={<Close />}>閉じる</DsButton>{mode === 'detail' && selected && <DsButton tone="primary" onClick={() => { setForm({ name: selected.name, email: selected.email, password: '', role: selected.role, doctor_id: selected.doctor_id ?? '', is_active: selected.is_active }); setMode('edit'); }}>編集する</DsButton>}{mode !== 'detail' && <DsButton tone="primary" onClick={() => void save()} disabled={saving}>{saving ? '保存中...' : '保存する'}</DsButton>}</DialogActions>
    </Dialog>
  </div>;
};

export default UserManagementPage;
